<script lang="ts">
export default {
  name: 'DOBenchmarking',
};
</script>

<script setup lang="ts">
import { benchmarkService, referenceService } from '@/services/api';
import { Account } from '@/types/Account';
import { Benchmark, DOBenchmark, emptyBenchmark } from '@/types/Benchmark';
import { ref, computed, onMounted, watch } from 'vue';
import { AttributeOption, both, calc, Chart, Column, PUBLIC, sameOwnershipType, SHOW_ALL } from './Benchmarking';
import BenchmarkingCharts from './BenchmarkingCharts.vue';
import BenchmarkingTable from './BenchmarkingTable.vue';
import { compactMoney } from '@/utilities/Money';
import AccountBenchmarkViewOptions from '@/components/AccountBenchmarkViewOptions.vue';
import { calcAverage, calcMedian } from '@/utilities/Number';
import GenericMultiFilter, { type GenericMultiFilterToggleValue } from '@/components/v2/GenericMultiFilter.vue';
import { Industry } from '@/types/Industry';
import { LabelValue } from '@/types/label-value';
import { useRoute } from 'vue-router';
import GenericRangeFilter from '@/components/v2/GenericRangeFilter.vue';

const props = defineProps<{
  account: Account;
}>();

const route = useRoute();

// -----
// data
// -----
const isLoading = ref(false);
const accountData = ref<DOBenchmark[]>([]);
const data = ref([] as DOBenchmark[]);
const selectedClients = ref([] as string[]);
const appliedClients = ref([] as string[]);
const appliedLabelDisplay = ref(SHOW_ALL);
const appliedMarketCapRange = ref(undefined as undefined | number[]);
/** Market cap filter UI value in millions (for GenericRangeFilter v-model). */
const marketCapFilterValue = ref<number[]>([]);
const appliedCompanyAssetsRange = ref(undefined as undefined | number[]);
const revenueFilterRange = ref(undefined as undefined | number[]);
const attributeTypes = ref([] as AttributeOption[]);
const selectedAttributeTypes = ref<GenericMultiFilterToggleValue[]>([]);
const industries = ref([] as LabelValue[]);
const selectedIndustries = ref([] as number[]);

function transformAccountName(d: DOBenchmark): DOBenchmark {
  return { ...d, accountName: appliedLabelDisplay.value === SHOW_ALL ? d.accountName : d.accountNameAlt };
}

function filterByMarketCapRange(d: DOBenchmark): boolean {
  if (appliedMarketCapRange.value) {
    const min = appliedMarketCapRange.value[0];
    const max = appliedMarketCapRange.value[1];
    return d.marketCap2YearHigh >= min && d.marketCap2YearHigh <= max;
  }
  return true;
}

function filterByCompanyAssetsRange(d: DOBenchmark): boolean {
  if (appliedCompanyAssetsRange.value) {
    if (d.companyAssets === undefined) return false;

    const min = appliedCompanyAssetsRange.value[0];
    const max = appliedCompanyAssetsRange.value[1];
    return d.companyAssets >= min && d.companyAssets <= max;
  }
  return true;
}

function filterByRevenueRange(d: DOBenchmark): boolean {
  if (revenueFilterRange.value) {
    if (d.revenueTTM === undefined) return false;

    const min = revenueFilterRange.value[0];
    const max = revenueFilterRange.value[1];
    return d.revenueTTM >= min && d.revenueTTM <= max;
  }
  return true;
}

function applyClientFilter(): void {
  appliedClients.value = selectedClients.value;
}

function applyViewOptions(selectedLabels: string): void {
  appliedLabelDisplay.value = selectedLabels;
}

async function refresh(): Promise<void> {
  isLoading.value = true;

  const includedAttributeIds = selectedAttributeTypes.value.filter((t) => t.isIncluded).map((t) => Number(t.value));
  const excludedAttributeIds = selectedAttributeTypes.value.filter((t) => !t.isIncluded).map((t) => Number(t.value));

  const [benchmarkData, attrTypes, industriesRefData] = await Promise.all([
    benchmarkService.dno(props.account.ownershipType, includedAttributeIds, excludedAttributeIds, selectedIndustries.value),
    referenceService.accountAttributeTypes(),
    referenceService.industries(),
  ]);

  attributeTypes.value = attrTypes
    .filter((t) => sameOwnershipType(t, props.account.ownershipType) || both(t))
    .map((t) => ({ label: t.name, value: t.id }));
  data.value = (benchmarkData || []).map((d, i) => ({
    ...d,
    packagedCoverageTypes: `Packaged with ${d.packagedCoverageTypes}`,
    accountNameAlt: `Company ${i + 1}`,
  }));
  industries.value = industriesRefData.map((i: Industry) => ({ label: i.name, value: i.ID }));

  appliedMarketCapRange.value = undefined;
  marketCapFilterValue.value = [];

  // upon first load, save off the accountData to populate the first row of the benchmarking table

  if (accountData.value.length === 0) {
    const match = data.value.filter((d) => d.accountId === route.params.id);

    if (match.length > 0) {
      const account = match.reduce(
        (obj, d) => {
          if (!obj.policyYear) return d;
          if (!d.policyYear) return obj;
          if (obj.policyYear < d.policyYear) obj = d;
          return obj;
        },
        { ...match[0] },
      );
      accountData.value = [{ ...account, frozen: true }];
    }
  }

  isLoading.value = false;
}

function marketCapChanged(range: number[]): void {
  // the FilterSlider :values is represented in millions data model is not -> converting it back
  appliedMarketCapRange.value = range.length === 2 ? [range[0] * 1000000, range[1] * 1000000] : undefined;
}

function companyAssetsChanged(range: number[]): void {
  appliedCompanyAssetsRange.value = range.length === 2 ? [...range] : undefined;
}

function revenueChanged(range: number[]): void {
  revenueFilterRange.value = range.length === 2 ? [...range] : undefined;
}

function normalize(val: string): string {
  return val.trim().toLowerCase();
}

// ----------
// computed
// ----------

/** Bounds for market cap filter (in millions); used to pre-populate the range inputs. */
const marketCapRange = computed((): number[] => {
  const vals = data.value.filter((d) => d.marketCap2YearHigh).map((d) => d.marketCap2YearHigh);
  return [0, vals.length === 0 ? 0 : Math.ceil(Math.max(...vals) / 1_000_000)];
});

/** Bounds for revenue filter; used to pre-populate the range inputs. */
const revenueRange = computed((): number[] => {
  const vals = data.value.filter((d) => d.revenueTTM).map((d) => d.revenueTTM);
  return [0, vals.length === 0 ? 0 : Math.max(...vals)];
});

/** Bounds for company assets filter; used to pre-populate the range inputs. */
const assetRange = computed((): number[] => {
  const vals = data.value.filter((d) => d.companyAssets).map((d) => d.companyAssets) as number[];
  return [0, vals.length === 0 ? 0 : Math.max(...vals)];
});

const clients = computed((): { text: string; value: string }[] => {
  const clients = data.value.reduce(
    (acc, d) => {
      // is it the current client?
      if (d.accountId === route.params.id) return acc;

      // already in accumulator?
      if (acc.some((i) => d.accountName && normalize(i.text) === normalize(d.accountName))) {
        return acc;
      }
      if (props.account.ownershipType !== d.ownershipType) {
        return acc;
      }

      acc.push({ text: d.accountName || '', value: d.accountName || '' });
      return acc;
    },
    [] as { text: string; value: string }[],
  );

  clients.sort((a, b) => a.text.localeCompare(b.text));
  return clients;
});

const clientOptions = computed(() => clients.value.map((c) => ({ label: c.text, value: c.value })));

const otherData = computed((): DOBenchmark[] => {
  if (appliedClients.value.length > 0) {
    return data.value
      .filter((d) => appliedClients.value.includes(d.accountName || ''))
      .filter((d) => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
      .filter(filterByMarketCapRange)
      .filter(filterByRevenueRange)
      .filter(filterByCompanyAssetsRange)
      .map(transformAccountName) as DOBenchmark[];
  }
  return data.value
    .filter((d) => d.accountId !== route.params.id)
    .filter((d) => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
    .filter(filterByMarketCapRange)
    .filter(filterByRevenueRange)
    .filter(filterByCompanyAssetsRange)
    .map(transformAccountName) as DOBenchmark[];
});

const averageData = computed((): DOBenchmark[] => {
  return [
    {
      ...emptyBenchmark,
      key: 'avg',
      industry: 'Average',
      marketCap2YearHigh: calc(otherData.value, 'marketCap2YearHigh', calcAverage),
      marketCapMostRecent: calc(otherData.value, 'marketCapMostRecent', calcAverage),
      revenueTTM: calc(otherData.value, 'revenueTTM', calcAverage),
      companyAssets: calc(otherData.value, 'companyAssets', calcAverage),
      totalRetention: calc(otherData.value, 'totalRetention', calcAverage, true),
      totalDOLimit: calc(otherData.value, 'totalDOLimit', calcAverage),
      totalABCLimit: calc(otherData.value, 'totalABCLimit', calcAverage),
      totalSideA: calc(otherData.value, 'totalSideA', calcAverage),
      totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcAverage),
      maxPrimaryPPMAnnualized: calc(otherData.value, 'maxPrimaryPPMAnnualized', calcAverage),
      firstLayerILF: calc(otherData.value, 'firstLayerILF', calcAverage),
    },
  ];
});

const medianData = computed((): DOBenchmark[] => {
  return [
    {
      ...emptyBenchmark,
      key: 'med',
      industry: 'Median',
      marketCap2YearHigh: calc(otherData.value, 'marketCap2YearHigh', calcMedian),
      marketCapMostRecent: calc(otherData.value, 'marketCapMostRecent', calcMedian),
      revenueTTM: calc(otherData.value, 'revenueTTM', calcMedian),
      companyAssets: calc(otherData.value, 'companyAssets', calcMedian),
      totalRetention: calc(otherData.value, 'totalRetention', calcMedian, true),
      totalDOLimit: calc(otherData.value, 'totalDOLimit', calcMedian),
      totalABCLimit: calc(otherData.value, 'totalABCLimit', calcMedian),
      totalSideA: calc(otherData.value, 'totalSideA', calcMedian),
      totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcMedian),
      maxPrimaryPPMAnnualized: calc(otherData.value, 'maxPrimaryPPMAnnualized', calcMedian),
      firstLayerILF: calc(otherData.value, 'firstLayerILF', calcMedian),
    },
  ];
});

const isPublic = computed((): boolean => {
  return props.account.ownershipType?.trim().toLowerCase() === PUBLIC;
});

const isPrivate = computed((): boolean => {
  return !isPublic.value;
});

const titleText = computed((): string => {
  return `${isPublic.value ? 'Public' : 'Private'} D&O Benchmarking`;
});

const columns = computed((): Column<DOBenchmark>[] => {
  const columns: (Column<DOBenchmark> | undefined)[] = [
    { fieldName: 'policyYear', type: 'number', title: 'Year' },
    {
      fieldName: 'accountName',
      type: 'nosort',
      title: 'Client',
      classes: 'truncated',
      url: (data) => `/account/${data.accountId}/overview`,
      showInfoIcon: 'isPackaged',
      showInfoFieldName: 'packagedCoverageTypes',
    },
    { fieldName: 'industry', type: '', title: 'Industry', url: (data) => `/account/${data.accountId}/edit`, accessoryIcon: 'pencil' },
    isPublic.value
      ? {
          fieldName: 'marketCap2YearHigh',
          type: 'compactmoney',
          title: 'Market Cap (2-Year)',
          filter: 'compactMoney',
          exposureCategory: 'Market Cap',
          accessoryIcon: 'pencil',
        }
      : undefined,
    isPublic.value
      ? {
          fieldName: 'marketCapMostRecent',
          type: 'compactmoney',
          title: 'Market Cap (Recent)',
          filter: 'compactMoney',
          exposureCategory: 'Market Cap',
          accessoryIcon: 'pencil',
        }
      : undefined,
    {
      fieldName: 'revenueTTM',
      type: 'compactmoney',
      title: 'Revenue',
      classes: isPrivate.value ? 'sorted' : '',
      filter: 'compactMoney',
      exposureCategory: 'Company Financials',
    },
    isPrivate.value
      ? {
          fieldName: 'companyAssets',
          type: 'compactmoney',
          title: 'Assets',
          filter: 'compactMoney',
          exposureCategory: 'Company Financials',
          exposureMetricId: 2,
        }
      : undefined,
    {
      fieldName: 'totalDOLimit',
      type: 'compactmoney',
      title: 'D&O Limits',
      filter: 'compactMoney',
      url: (data) => `/account/${data.accountId}/policy-detail`,
      accessoryIcon: 'pencil',
    },
    { fieldName: 'totalABCLimit', type: 'compactmoney', title: 'ABC Limits', filter: 'compactMoney' },
    { fieldName: 'totalSideA', type: 'compactmoney', title: 'Side A Limits', filter: 'compactMoney' },
    { fieldName: 'totalRetention', type: 'compactmoney', title: 'Retention', filter: 'compactMoney' },
    { fieldName: 'totalPrimaryLimit', type: 'compactmoney', title: 'Primary Limit', filter: 'compactMoney' },
    { fieldName: 'maxPrimaryPPMAnnualized', type: 'compactmoney', title: 'Primary PPM', filter: 'compactMoney', annualized: true },
    { fieldName: 'firstLayerILF', type: 'text-right', title: '1st XS D&O Rate', filter: 'percentage' },
  ];
  return columns.filter((x) => x) as Column<DOBenchmark>[];
});

const charts = computed((): Chart<DOBenchmark>[] => {
  const charts: (Chart<DOBenchmark> | undefined)[] = [
    isPublic.value
      ? {
          fieldName: 'marketCap2YearHigh',
          title: 'Market Capitalization (2-Year Max)',
        }
      : undefined,
    isPublic.value
      ? {
          fieldName: 'marketCapMostRecent',
          title: 'Market Capitalization (Most Recent)',
        }
      : undefined,
    {
      fieldName: 'revenueTTM',
      title: 'Revenue',
    },
    isPrivate.value
      ? {
          fieldName: 'companyAssets',
          title: 'Company Assets',
        }
      : undefined,
    {
      fieldName: 'totalDOLimit',
      title: 'D&O Limits',
    },
    {
      fieldName: 'totalABCLimit',
      title: 'ABC Limits',
    },
    {
      fieldName: 'totalSideA',
      title: 'Side A Limits',
    },
    {
      fieldName: 'totalRetention',
      title: 'Retention',
    },
    {
      fieldName: 'totalPrimaryLimit',
      title: 'Primary Limit',
    },
    {
      fieldName: 'maxPrimaryPPMAnnualized',
      title: 'Primary PPM (Annualized)',
    },
    {
      fieldName: 'firstLayerILF',
      title: '1st XS D&O Rate',
      isPercentage: true,
    },
  ];
  return charts.filter((x) => x) as Chart<DOBenchmark>[];
});

// Typed for BenchmarkingTable / BenchmarkingCharts (avoids template `as` casts)
const tableOtherData = computed(() => otherData.value as Benchmark[]);
const tableAverageData = computed(() => averageData.value as Benchmark[]);
const tableMedianData = computed(() => medianData.value as Benchmark[]);
const tableColumns = computed((): Column[] => columns.value as Column[]);
const chartsAccountData = computed(() => accountData.value as Benchmark[]);
const chartsOtherData = computed(() => otherData.value as Benchmark[]);
const chartsCharts = computed((): Chart[] => charts.value as Chart[]);

// ----------
// watchers
// ----------

watch(selectedAttributeTypes, () => refresh());
watch(selectedIndustries, () => refresh());

// ----------
// lifecycle
// ----------

onMounted(async () => refresh());
</script>

<template>
  <BenchmarkingTable
    :accountData="accountData"
    :otherData="tableOtherData"
    :averageData="tableAverageData"
    :medianData="tableMedianData"
    :columns="tableColumns"
    :loading="isLoading"
    :titleText="titleText"
    @needs-update="$emit('needs-update')">
    <template v-slot:charts>
      <BenchmarkingCharts
        :accountData="chartsAccountData"
        :otherData="chartsOtherData"
        :charts="chartsCharts" />
    </template>

    <template v-slot:filterbar>
      <slot name="filterbar"></slot>

      <GenericMultiFilter
        :options="clientOptions"
        v-model="selectedClients"
        title="Clients"
        chip-title-field="label"
        data-test="client-filter"
        data-test-prefix="filter-client"
        @update:model-value="applyClientFilter" />

      <GenericRangeFilter
        v-if="isPublic"
        v-model="marketCapFilterValue"
        title="Market Capitalization"
        min-title="Min (Millions)"
        max-title="Max (Millions)"
        :suggested-range="marketCapRange"
        :value-formatter-fn="(val) => (val != null ? compactMoney((val ?? 0) * 1_000_000) : '')"
        data-test="market-cap-filter"
        data-test-prefix="market-cap-filter"
        @update:model-value="marketCapChanged" />

      <GenericRangeFilter
        v-if="isPrivate"
        :model-value="revenueFilterRange ?? []"
        title="Revenue"
        :suggested-range="revenueRange"
        :value-formatter-fn="(val) => (val != null ? compactMoney(val) : '')"
        data-test="revenue-filter"
        data-test-prefix="revenue-filter"
        @update:model-value="revenueChanged" />

      <GenericRangeFilter
        v-if="isPrivate"
        :model-value="appliedCompanyAssetsRange ?? []"
        title="Company Assets"
        :suggested-range="assetRange"
        :value-formatter-fn="(val) => (val != null ? compactMoney(val) : '')"
        data-test="company-assets-filter"
        data-test-prefix="company-assets-filter"
        @update:model-value="companyAssetsChanged" />

      <GenericMultiFilter
        :options="industries"
        chip-title-field="label"
        v-model="selectedIndustries"
        title="Industries"
        data-test="industry-filter"
        data-test-prefix="industry-filter" />

      <GenericMultiFilter
        :options="attributeTypes"
        chip-title-field="label"
        v-model:toggle-value="selectedAttributeTypes"
        enable-include-exclude-toggle
        title="Client Attributes"
        data-test="client-type-filter"
        data-test-prefix="client-type-filter" />

      <AccountBenchmarkViewOptions
        :labels="appliedLabelDisplay"
        @change="applyViewOptions" />
    </template>
  </BenchmarkingTable>
</template>
